import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Globe, ExternalLink, Loader2, Command } from "lucide-react";
import { api } from "@/lib/api";

const PRESET_QUERIES = [
  "Latest Italian M&A deals 2026",
  "European family office consolidation packaging",
  "Private equity exits Italy this quarter",
  "Buy-and-build strategy industrial mid-market",
];

export default function WebSearchWidget() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const inputRef = useRef(null);

  // Cmd/Ctrl+K keyboard shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  const doSearch = async (q) => {
    const text = (q ?? query).trim();
    if (!text) return;
    setLoading(true);
    setResults([]);
    setMeta(null);
    try {
      const r = await api.post("/search/web", {
        query: text,
        topic: "finance",
        max_results: 8,
        search_depth: "basic",
        time_range: "month",
      });
      setResults(r.data.results || []);
      setMeta({ provider: r.data.provider, degraded: r.data.degraded });
    } catch (e) {
      setMeta({ error: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(true)}
        data-testid="web-search-trigger"
        title="Web Search (Cmd/Ctrl+K)"
        className="hidden md:flex fixed bottom-6 right-6 z-40 items-center gap-2 px-4 py-2.5 rounded-full transition group"
        style={{
          background: "linear-gradient(180deg, #C5A059, #a88645)",
          color: "#0b0b0e",
          boxShadow: "0 8px 24px rgba(197,160,89,0.35)",
        }}
      >
        <Search size={15} />
        <span className="text-[12px] font-medium">Web Search</span>
        <span className="hidden lg:inline-flex items-center gap-1 text-[10px] opacity-70 pl-1">
          <Command size={10} /> K
        </span>
      </button>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
              data-testid="web-search-overlay"
            />
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="fixed z-50 left-1/2 top-[10vh] -translate-x-1/2 w-[92vw] max-w-2xl"
              data-testid="web-search-modal"
            >
              <div className="rounded-lg overflow-hidden" style={{ background: "#0f0f13", border: "1px solid rgba(197,160,89,0.25)", boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}>
                {/* Search input row */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
                  <Globe size={16} className="text-gold shrink-0" />
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                    placeholder="Search M&A news, deals, sectors, companies..."
                    className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/30"
                    data-testid="web-search-input"
                  />
                  {loading && <Loader2 size={14} className="text-gold animate-spin" />}
                  <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white p-1" data-testid="web-search-close">
                    <X size={16} />
                  </button>
                </div>

                {/* Body */}
                <div className="max-h-[65vh] overflow-y-auto">
                  {results.length === 0 && !loading && (
                    <div className="p-5">
                      <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Suggestions</div>
                      <div className="grid gap-1.5">
                        {PRESET_QUERIES.map((p) => (
                          <button
                            key={p}
                            onClick={() => { setQuery(p); doSearch(p); }}
                            data-testid={`web-search-preset-${p.slice(0,10).replace(/\s/g,'-')}`}
                            className="text-left text-[12.5px] p-2.5 rounded-md bg-white/[0.02] border border-white/5 hover:border-[#C5A059]/40 text-white/70 transition flex items-center gap-2"
                          >
                            <Search size={12} className="text-white/30" />
                            {p}
                          </button>
                        ))}
                      </div>
                      {meta?.error && (
                        <div className="mt-4 text-[12px] text-red-400">Error: {meta.error}</div>
                      )}
                    </div>
                  )}

                  {results.length > 0 && (
                    <>
                      <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between text-[10px] uppercase tracking-wider text-white/40">
                        <span>{results.length} results</span>
                        {meta?.provider && (
                          <span className={meta.provider === "tavily-cache" ? "text-gold" : "text-emerald-400"}>
                            {meta.provider === "tavily-cache" ? "· cached" : "· live"}
                          </span>
                        )}
                      </div>
                      <div className="divide-y divide-white/5">
                        {results.map((r, i) => (
                          <a
                            key={r.url + i}
                            href={r.url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`web-search-result-${i + 1}`}
                            className="flex items-start gap-3 p-4 hover:bg-white/[0.02] transition group"
                          >
                            <span className="text-[10px] font-mono text-gold shrink-0 mt-0.5">[{i + 1}]</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] text-white group-hover:text-gold truncate">{r.title || r.url}</div>
                              <div className="text-[10px] text-white/40 truncate">{r.url}</div>
                              {r.content && (
                                <div className="text-[11.5px] text-white/60 mt-1 line-clamp-2">{r.content}</div>
                              )}
                              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-white/30">
                                {r.published_date && <span>{r.published_date}</span>}
                                {typeof r.score === "number" && <span>score {r.score.toFixed(2)}</span>}
                              </div>
                            </div>
                            <ExternalLink size={12} className="text-white/30 shrink-0 mt-1 group-hover:text-gold" />
                          </a>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between text-[10px] text-white/40">
                  <span className="flex items-center gap-1"><Globe size={10} className="text-gold" /> Powered by Tavily</span>
                  <span>Press <kbd className="px-1 py-0.5 bg-white/5 rounded border border-white/10">Esc</kbd> to close</span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
